import { Box, Typography } from '@mui/material';
import { SignedSafetyControlRecord } from '../../services/reportsApi';

function decisionText(record: SignedSafetyControlRecord): string {
  const next = record.signed_body.next_decision;
  if (typeof next === 'string' && next.trim()) return next.trim();
  if (next && typeof next === 'object' && !Array.isArray(next)) {
    for (const key of ['decision', 'immediate_action', 'follow_up_action'] as const) {
      const value = next[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }
  return 'Review the signed finding, retained control, and governed recheck before the next operating decision.';
}

export function DecisionBand({ record }: { record: SignedSafetyControlRecord }) {
  const { recheck, result } = record.signed_body;
  const comparable = recheck.comparable === true && result.comparability_met === true;
  const nextDecision = decisionText(record);
  const supports = comparable
    ? 'A bounded comparison of the retained baseline and recheck windows.'
    : 'The bounded finding and site-selected control retained in this revision.';
  const boundary = typeof result.does_not_establish?.[0] === 'string'
    && result.does_not_establish[0].trim()
    ? result.does_not_establish[0]
    : (comparable
      ? 'This record does not establish outcomes outside the signed scope or observation windows.'
      : 'This record does not establish improvement or control effectiveness.');

  return (
    <Box
      component="section"
      aria-labelledby="decision-band-heading"
      data-testid="decision-band"
      sx={{
        bgcolor: '#0b3154',
        color: '#f8fbff',
        px: { xs: 2.5, md: 4 },
        py: { xs: 3, md: 3.5 },
      }}
    >
      <Typography
        id="decision-band-heading"
        variant="overline"
        sx={{ color: '#68ddff', letterSpacing: '0.15em', fontWeight: 800 }}
      >
        Operating decision
      </Typography>
      <Box
        sx={{
          mt: 1.5,
          display: 'grid',
          gap: { xs: 2.5, md: 4 },
          gridTemplateColumns: { xs: '1fr', md: '1.35fr 1fr 1fr' },
        }}
      >
        {[
          ['Next decision', nextDecision],
          ['This record supports', supports],
          ['This record does not establish', boundary],
        ].map(([label, value]) => (
          <Box key={label}>
            <Typography
              variant="caption"
              sx={{ color: '#68ddff', textTransform: 'uppercase', letterSpacing: '0.08em' }}
            >
              {label}
            </Typography>
            <Typography sx={{ mt: 0.7, lineHeight: 1.55 }}>{value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
